import React, { useState, Fragment } from 'react';
import { 
  CheckCircleIcon, 
  ClockIcon,
  EllipsisVerticalIcon,
  UserIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { 
  CheckCircleIcon as CheckCircleSolidIcon,
  ClockIcon as ClockSolidIcon
} from '@heroicons/react/24/solid';
import { Menu, Transition } from '@headlessui/react';
import { formatDate } from '../utils/dateUtils';
import Avatar from './Avatar';

interface TeamMember {
  user_id: string;
  email: string;
  role: string;
  status: 'active' | 'pending_approval';
  joined_at: string;
  last_login: string;
}

interface TeamMemberRowProps {
  member: TeamMember;
  onApprove: (userId: string) => void;
  onRemove: (userId: string, email: string) => void;
  onRoleChange: (userId: string, newRole: string) => void;
  isAdmin: boolean;
}

const TeamMemberRow: React.FC<TeamMemberRowProps> = ({
  member,
  onApprove,
  onRemove,
  onRoleChange,
  isAdmin
}) => {
  const [showRoleChange, setShowRoleChange] = useState(false);

  // Get role display info
  const getRoleInfo = (role: string) => {
    switch (role) {
      case 'tenant_owner':
        return { text: 'Owner', icon: ShieldCheckIcon, color: 'text-purple-600 bg-purple-100 border-purple-200 dark:text-purple-300 dark:bg-purple-900/20 dark:border-purple-800' };
      case 'tenant_admin':
        return { text: 'Admin', icon: ShieldCheckIcon, color: 'text-blue-600 bg-blue-100 border-blue-200 dark:text-blue-300 dark:bg-blue-900/20 dark:border-blue-800' };
      case 'team_member':
        return { text: 'Member', icon: UserGroupIcon, color: 'text-green-600 bg-green-100 border-green-200 dark:text-green-300 dark:bg-green-900/20 dark:border-green-800' };
      case 'pending_member':
        return { text: 'Pending', icon: ClockIcon, color: 'text-yellow-600 bg-yellow-100 border-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/20 dark:border-yellow-800' };
      default:
        return { text: role, icon: UserIcon, color: 'text-muted-foreground bg-muted border-border' };
    }
  };

  // Get status display info
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'active':
        return { 
          text: 'Active', 
          icon: CheckCircleSolidIcon, 
          color: 'text-green-600 bg-green-100 border-green-200 dark:text-green-300 dark:bg-green-900/20 dark:border-green-800'
        };
      case 'pending_approval':
        return { 
          text: 'Pending Approval', 
          icon: ClockSolidIcon, 
          color: 'text-yellow-600 bg-yellow-100 border-yellow-200 dark:text-yellow-300 dark:bg-yellow-900/20 dark:border-yellow-800'
        };
      default:
        return { 
          text: status, 
          icon: UserIcon, 
          color: 'text-muted-foreground bg-muted border-border'
        };
    }
  };

  const roleInfo = getRoleInfo(member.role);
  const statusInfo = getStatusInfo(member.status);
  const RoleIcon = roleInfo.icon;
  const StatusIcon = statusInfo.icon;

  const handleActionClick = (action: string) => {
    switch (action) {
      case 'approve':
        onApprove(member.user_id);
        break;
      case 'remove':
        onRemove(member.user_id, member.email);
        break;
      case 'role':
        setShowRoleChange(true);
        break;
    }
  };

  const handleRoleChange = (newRole: string) => {
    onRoleChange(member.user_id, newRole);
    setShowRoleChange(false);
  };

  return (
    <div className="bg-card hover:bg-muted/50 transition-all duration-200 border-b border-border last:border-b-0">
      <div className="px-6 py-5">
        <div className="flex items-center space-x-8">
          {/* User Info Section - Left-aligned */}
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            <Avatar email={member.email} size="md" />
            
            {/* User Details */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {member.email}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                ID: {member.user_id.slice(0, 8)}...
              </p>
            </div>
          </div>

          {/* Role Section - Center-aligned */}
          <div className="flex-shrink-0">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${roleInfo.color}`}>
              <RoleIcon className="h-4 w-4 mr-2" />
              {roleInfo.text}
            </span>
          </div>

          {/* Status Section - Center-aligned */}
          <div className="flex-shrink-0">
            <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${statusInfo.color}`}>
              <StatusIcon className="h-4 w-4 mr-2" />
              {statusInfo.text}
            </span>
          </div>

          {/* Date Info Section - Center-aligned */}
          <div className="flex flex-col space-y-1 flex-shrink-0 min-w-0">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Joined:</span> {formatDate(member.joined_at)}
            </p>
            <p className="text-sm text-muted-foreground">
              <span className="font-medium">Last Login:</span> {formatDate(member.last_login)}
            </p>
          </div>

          {/* Actions Section - Right-aligned */}
          {isAdmin && (
            <div className="flex-shrink-0">
              <Menu as="div" className="relative">
                <Menu.Button className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-full transition-colors duration-200">
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </Menu.Button>
                
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 bg-background rounded-lg shadow-lg border border-border z-10 focus:outline-none">
                    <div className="py-1">
                      {member.status === 'pending_approval' && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleActionClick('approve')}
                              className={`${
                                active ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' : 'text-green-700 dark:text-green-300'
                              } flex items-center w-full px-4 py-2.5 text-sm transition-colors duration-200`}
                            >
                              <CheckCircleIcon className="h-4 w-4 mr-3" />
                              Approve Member
                            </button>
                          )}
                        </Menu.Item>
                      )}
                      
                      {member.status === 'active' && member.role !== 'tenant_owner' && (
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => handleActionClick('role')}
                              className={`${
                                active ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-blue-700 dark:text-blue-300'
                              } flex items-center w-full px-4 py-2.5 text-sm transition-colors duration-200`}
                            >
                              <ShieldCheckIcon className="h-4 w-4 mr-3" />
                              Change Role
                            </button>
                          )}
                        </Menu.Item>
                      )}

                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => handleActionClick('remove')}
                            className={`${
                              active ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'text-red-700 dark:text-red-300'
                            } flex items-center w-full px-4 py-2.5 text-sm transition-colors duration-200`}
                          >
                            <UserIcon className="h-4 w-4 mr-3" />
                            {member.status === 'pending_approval' ? 'Reject' : 'Remove'}
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* Role Change Dropdown */}
              {showRoleChange && (
                <div className="absolute right-0 mt-2 w-48 bg-background rounded-lg shadow-lg border border-border z-10">
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b border-border">
                      Select New Role
                    </div>
                    {['tenant_admin', 'team_member', 'viewer'].map((role) => (
                      <button
                        key={role}
                        onClick={() => handleRoleChange(role)}
                        className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors duration-200"
                      >
                        {role === 'tenant_admin' && <ShieldCheckIcon className="h-4 w-4 mr-2" />}
                        {role === 'team_member' && <UserGroupIcon className="h-4 w-4 mr-2" />}
                        {role === 'viewer' && <EyeIcon className="h-4 w-4 mr-2" />}
                        {role === 'tenant_admin' ? 'Admin' : role === 'team_member' ? 'Member' : 'Viewer'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close role change dropdown */}
      {showRoleChange && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowRoleChange(false)}
        />
      )}
    </div>
  );
};

export default TeamMemberRow;
